import { AdminFormRow } from '@ui';
import { useCallback } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { AvatarInput, Checkbox, Divider } from '@ds';
import { MainContactForm } from '@features/forms/MainContactForm';
import { useOpoByIdQuery } from '@/queries/opo';
import { useOpobffOrganizationQuery } from '@/queries/opobff';
import { TMDXFile } from '@/api/apis/files';
import { LOGO_IMAGE_TYPES } from '@/constants/file.constants';
import { getFullName } from '@/utils/helpers';
import { CreateOpoContactFormValues } from './config';
import { CreateOpoContactEmailField } from './CreateOpoContactEmailField';
import { CreateOpoContactRolesField } from './CreateOpoContactRolesField';

type CreateOpoContactFormProps = {
  showInviteAsUser?: boolean;
  useOpobffContact?: boolean;
};

export const CreateOpoContactForm = ({
  showInviteAsUser = false,
  useOpobffContact = false,
}: CreateOpoContactFormProps) => {
  const { setValue, getValues, clearErrors, trigger, control } = useFormContext<CreateOpoContactFormValues>();

  const [firstName, lastName, photoPath, parentId, inviteAsUser] = useWatch({
    control,
    name: ['firstName', 'lastName', 'photoPath', 'parentId', 'inviteAsUser'],
  });

  const isInviteAsUserSelected = Boolean(inviteAsUser);

  const { data: opobffOrgData } = useOpobffOrganizationQuery({ enabled: useOpobffContact });
  const { data: opoServiceData } = useOpoByIdQuery(useOpobffContact ? null : parentId || '');
  const opoData = useOpobffContact ? opobffOrgData : opoServiceData;

  const onImageChange = useCallback(
    (file: TMDXFile) => {
      setValue('photo', file, { shouldDirty: true });
    },
    [setValue],
  );

  const handleInviteAsUserChange = useCallback(
    (isSelected: boolean) => {
      setValue('inviteAsUser', isSelected, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (parentId) {
        const currentRoles = getValues('memberships.0.roles') ?? [];

        setValue(
          'memberships',
          [
            {
              opoOrganizationId: parentId,
              roles: isSelected ? currentRoles : [],
            },
          ],
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        );
      }

      if (isSelected) {
        trigger(['firstName', 'lastName', 'email', 'memberships']).catch(() => null);
        return;
      }

      clearErrors(['firstName', 'lastName', 'email', 'memberships']);
    },
    [clearErrors, getValues, parentId, setValue, trigger],
  );

  return (
    <div className="flex items-start gap-10">
      <AvatarInput
        src={photoPath}
        radius="xxl"
        disabled={false}
        isBordered
        color="primary"
        size="xxl"
        name={getFullName(firstName, lastName)}
        onFileChange={onImageChange}
        accept={LOGO_IMAGE_TYPES}
      />

      <div className="grow gap-5 flex flex-col">
        <AdminFormRow title="OPO Name">
          {opoData && (
            <span className="text-sm">
              {opoData.code} - {opoData.name}
            </span>
          )}
        </AdminFormRow>

        <MainContactForm
          showEmail={false}
          requiredFields={{
            firstName: isInviteAsUserSelected,
            lastName: isInviteAsUserSelected,
          }}
        />

        {showInviteAsUser && (
          <>
            <Divider orientation="horizontal" />

            <Controller
              name="inviteAsUser"
              control={control}
              render={({ field, fieldState: { invalid } }) => (
                <Checkbox
                  isSelected={Boolean(field.value)}
                  onValueChange={handleInviteAsUserChange}
                  isInvalid={invalid}
                  className="p-0"
                >
                  Invite OPO Contact as OPO User
                </Checkbox>
              )}
            />

            {isInviteAsUserSelected && (
              <>
                <CreateOpoContactEmailField />
                <CreateOpoContactRolesField />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
